import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

const FACTOR_KEYS = [
  { key: 'smoking', tKey: 'cvrisk_factor_smoking', points: 2 },
  { key: 'hypertension', tKey: 'cvrisk_factor_hypertension', points: 2 },
  { key: 'diabetes', tKey: 'cvrisk_factor_diabetes', points: 2 },
  { key: 'cholesterol', tKey: 'cvrisk_factor_cholesterol', points: 1 },
  { key: 'obesity', tKey: 'cvrisk_factor_obesity', points: 1 },
  { key: 'sedentary', tKey: 'cvrisk_factor_sedentary', points: 1 },
  { key: 'family', tKey: 'cvrisk_factor_family', points: 2 },
];

const CardiovascularRisk = () => {
  const { t } = useLanguage();
  const FACTORS = FACTOR_KEYS.map((f) => ({ ...f, label: t(f.tKey) }));
  const [age, setAge] = useState('');
  const [sex, setSex] = useState('');
  const [checked, setChecked] = useState({});

  const toggle = (key) => setChecked((c) => ({ ...c, [key]: !c[key] }));

  const ageNum = parseInt(age, 10) || 0;
  let score = FACTORS.reduce((sum, f) => sum + (checked[f.key] ? f.points : 0), 0);
  if (ageNum >= 65) score += 3;
  else if (ageNum >= 50) score += 2;
  else if (ageNum >= 40) score += 1;
  if (sex === 'homme' && ageNum >= 45) score += 1;

  let band = null;
  if (age && sex) {
    if (score <= 3) band = { label: t('cvrisk_band_low'), color: '#10B981', low: true };
    else if (score <= 7) band = { label: t('cvrisk_band_moderate'), color: '#F59E0B', low: false };
    else band = { label: t('cvrisk_band_high'), color: '#EF4444', low: false };
  }

  return (
    <ToolPageLayout
      title={t('cvrisk_title')}
      subtitle={t('cvrisk_subtitle')}
      specialty="cardiologue"
    >
      <div className="bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-2xl p-4 flex gap-3 mb-8">
        <AlertTriangle className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" />
        <p className="text-sm text-[#92400E]">
          {t('cvrisk_warning')}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div>
          <Label className="mb-1 block">{t('cvrisk_age_label')}</Label>
          <Input type="number" min="1" max="120" value={age} onChange={(e) => setAge(e.target.value)} className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('cvrisk_sex_label')}</Label>
          <select
            value={sex}
            onChange={(e) => setSex(e.target.value)}
            className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE] bg-white h-10"
          >
            <option value="">{t('cvrisk_sex_placeholder')}</option>
            <option value="homme">{t('cvrisk_sex_male')}</option>
            <option value="femme">{t('cvrisk_sex_female')}</option>
          </select>
        </div>
      </div>

      <div className="space-y-3">
        {FACTORS.map((f) => (
          <label
            key={f.key}
            className="flex items-center gap-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3 cursor-pointer hover:border-[#0596DE]/40"
          >
            <input
              type="checkbox"
              checked={!!checked[f.key]}
              onChange={() => toggle(f.key)}
              className="w-4 h-4 accent-[#0596DE]"
            />
            <span className="text-sm text-[#0F172A]">{f.label}</span>
          </label>
        ))}
      </div>

      {band && (
        <div className="mt-8 rounded-2xl p-6 text-center border" style={{ borderColor: `${band.color}40`, backgroundColor: `${band.color}0D` }}>
          <div className="text-sm text-[#64748B] mb-1">{t('cvrisk_band_heading')}</div>
          <div className="text-2xl font-bold" style={{ color: band.color }}>{band.label}</div>
          <p className="text-sm text-[#64748B] mt-3 max-w-md mx-auto">
            {band.low ? t('cvrisk_msg_low') : t('cvrisk_msg_other')}
          </p>
        </div>
      )}

      <p className="mt-6 text-sm text-[#64748B]">
        {t('cvrisk_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default CardiovascularRisk;
